import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import API from "../../../util/api";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

import ActionMenu from "./ActionMenu";
import TeacherList from "./TeacherList";
import AddTeacherForm from "./AddTeacherForm";
import RemoveTeacher from "./RemoveTeacher";
import ImportTeacherList from "./ImportTeacherList";
import MassCertifyModal from "./MassCertifyModal";
import MassUncertifyModal from "./MassUncertifyModal";
import SendWelcomeEmailModal from "./SendWelcomeEmailModal";
import TeacherDetailsDisplay from "./TeachersDetailsDisplay";
import AdminResetPassword from "./AdminResetPassword";
import StatsUploadContent from "./StatUploadContent";
import { selfImplementation as triggerSelfImplementation } from "../../../sagas/actions";


const breakpoints = {
  tablet: 768,
  phone:  480,
};

const VIEW_GAP = "1.5em";

const Container = styled.div`
  background: #f8f8f8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);
  padding: 1.5em;
  display: flex;
  flex-wrap: wrap;
  gap: ${VIEW_GAP};
  overflow: hidden;

  @media (max-width: ${breakpoints.tablet}px) {
    gap: 1.25em;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 1.25em;
    gap: 1em;
  }
`;
const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;

  @media (max-width: ${breakpoints.tablet}px) {
    padding-top: 40px;
  }
`;

const SelfImplementationToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};
  margin-bottom: 16px;
  cursor: pointer;
  flex-wrap: nowrap;

  @media (max-width: ${breakpoints.tablet}px) {
    width: 100%;
  }

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  span:first-of-type {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

const SelfImplementationNote = styled.span`
  margin-left: 12px;
  font-size: 13px;
  color: ${({ colors }) => colors.labelText};
`;

export default function TeachersContent({ colors }) {
   const dispatch = useDispatch();
   const location = useLocation();
 const [mode, setMode] = useState(location.state?.mode || "list");
 const locationMode = location.state?.mode;
 
  const [teachers, setTeachers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selfImplLoading, setSelfImplLoading] = useState(false);

  // pagination + sort + search
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm);

  // other modes
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [massCertOpen, setMassCertOpen] = useState(false);
  const [massUnCertOpen, setMassUnCertOpen] = useState(false);
  const [welcomeEmailOpen, setWelcomeEmailOpen] = useState(false);

  // Reset the panel whenever the location changes (e.g., Teacher tab clicks).
  useEffect(() => {
    const nextMode = locationMode || "list";
    setMode(nextMode);
    if (nextMode !== "detail") {
      setSelectedTeacher(null);
    }
  }, [location.key, locationMode]);

  const selectedSubscription =
    useSelector((s) => s.auth.selected_subscriptions) || {};
  const { invoice_id, customer_id, customer_code } = selectedSubscription;
  const userId = useSelector((s) => s.auth.user?.user_id);
  const selfImplemented = useSelector((s) => {
    const rootValue = s.auth?.self_implemented;
    if (rootValue === 0 || rootValue === 1) return rootValue;
    return s.auth?.user?.self_implemented || 0;
  });
  const selfImplementationNotice = useSelector(
    (s) => s.auth?.self_implementation_notice
  );
  const isSelfImplemented = Number(selfImplemented) === 1;
  const canToggleSelfImplementation = Boolean(userId);
  const latestNoticeTimestamp = useRef(null);

  const fetchTeachers = useCallback(() => {
    if (!invoice_id) return;

    setLoading(true);
    API.getSubscriptionTeachers({
      invoice_id,
      customer_id,
      customer_code,
      page: page - 1,
      items_per_page: itemsPerPage,
      order: sortOrder.toUpperCase(),
      order_by: sortKey,
      search: debouncedSearchTerm,
    })
      .then((resp) => {
        const rows = (resp.teachers || []).map((t) => ({
          ...t,
          students_count: Number(t.num_of_students || 0),
          grade_records: t.grade_codes || [],
        }));
        setTeachers(rows);
        setTotal(resp.total_teachers || 0);
      })
      .catch((err) => {
        console.error("Failed to load teachers:", err);
        setTeachers([]);
        setTotal(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    invoice_id,
    customer_id,
    customer_code,
    page,
    itemsPerPage,
    sortKey,
    sortOrder,
    debouncedSearchTerm,
  ]);

  // reload on any dependency change
  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handleRemove = (implementation_id) => {
    setTeachers((ts) =>
      ts.filter((t) => t.implementation_id !== implementation_id)
    );
  };

  const handleSelfImplementationChange = useCallback(
    (checked) => {
      if (!userId) return;
      if (selfImplLoading) return;
      setSelfImplLoading(true);
      dispatch(
        triggerSelfImplementation({
          user_id: userId,
          value: checked ? 1 : 0,
        })
      );
    },
    [dispatch, userId, selfImplLoading]
  );

  useEffect(() => {
    if (!selfImplementationNotice) return;
    const { timestamp } = selfImplementationNotice || {};
    if (!timestamp || latestNoticeTimestamp.current === timestamp) {
      return;
    }

    latestNoticeTimestamp.current = timestamp;
    setSelfImplLoading(false);
  }, [selfImplementationNotice]);

  const handleShowDetails = (t) => {
    setSelectedTeacher(t);
    setMode("detail");
  };
  const handleBack = () => {
    setMode("list");
    setSelectedTeacher(null);
    fetchTeachers();
  };

  const selectedTeacherImplementations = useMemo(() => {
    if (!selectedTeacher) return [];
    const seen = new Set();
    const matches = teachers.filter(
      (impl) =>
        impl.user_id === selectedTeacher.user_id &&
        impl.customer_user_id === selectedTeacher.customer_user_id
    ).filter((impl) => {
      const key = impl.implementation_id || `${impl.user_id}-${impl.customer_user_id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    if (matches.length === 0) {
      return [selectedTeacher];
    }
    return matches;
  }, [teachers, selectedTeacher]);

  return (
    <Container>
      <ActionMenu
        colors={colors}
        onModeChange={setMode}
        selectedTeacher={selectedTeacher}
        mode={mode}
        onMassCertify={() => setMassCertOpen(true)}
        onMassUncertify={() => setMassUnCertOpen(true)}
        onSendWelcomeEmails={() => setWelcomeEmailOpen(true)}
      />

      <View>
        {mode === "list" && (
          <>
            <SelfImplementationToggle colors={colors}>
              <input
                type="checkbox"
                checked={isSelfImplemented}
                onChange={(event) =>
                  handleSelfImplementationChange(event.target.checked)
                }
                disabled={selfImplLoading || !canToggleSelfImplementation}
              />
              <span>
                I will be implementing the ISAFE lesson plans and curriculum myself.
              </span>
              {selfImplLoading && (
                <SelfImplementationNote colors={colors}>
                  Saving...
                </SelfImplementationNote>
              )}
            </SelfImplementationToggle>

            <TeacherList
              data={teachers}
              total={total}
              colors={colors}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={(n) => {
                setItemsPerPage(n);
                setPage(1);
              }}
              page={page}
              setPage={setPage}
              searchTerm={searchTerm}
              setSearchTerm={(val) => {
                setSearchTerm(val);
                setPage(1);
              }}
              sortKey={sortKey}
              sortOrder={sortOrder}
              setSortKey={(k) => {
                setSortKey(k);
                setPage(1);
              }}
              setSortOrder={(o) => {
                setSortOrder(o);
                setPage(1);
              }}
              onRemoveClick={handleRemove}
              onTeacherUpdated={fetchTeachers}
              onShowDetails={handleShowDetails}
              loading={loading}
            />
          </>
        )}

        {mode === "import" && (
          <ImportTeacherList
            colors={colors}
            onBack={() => {
              setMode("list");
              fetchTeachers();
            }}
          />
        )}
        {mode === "add" && (
          <AddTeacherForm
            colors={colors}
            onCancel={() => setMode("list")}
            onAdded={() => {
              setMode("list");
              fetchTeachers();
            }}
          />
        )}
        {mode === "remove" && (
          <RemoveTeacher
            data={teachers}
            colors={colors}
            onRemoveClick={handleRemove}
          />
        )}

        {mode === "upload" && (
          <StatsUploadContent
            colors={colors}
          />
        )}
        {mode === "detail" && selectedTeacher && (
          <TeacherDetailsDisplay
            teacher={selectedTeacher}
            subscription={selectedSubscription}
            implementations={selectedTeacherImplementations}
            colors={colors}
            onBack={handleBack}
            onChangePassword={() => setMode("resetPassword")}
          />
        )}
        {mode === "resetPassword" && selectedTeacher && (
          <AdminResetPassword
            teacher={selectedTeacher}
            colors={colors}
            onCancel={() => setMode("detail")}
            onComplete={() => setMode("detail")}
          />
        )}
      </View>

      <MassCertifyModal
        show={massCertOpen}
        invoiceId={invoice_id}
        teachers={teachers}
        onCancel={() => setMassCertOpen(false)}
        onConfirm={() => {
          setMassCertOpen(false);
          fetchTeachers();
        }}
      />

      <MassUncertifyModal
        show={massUnCertOpen}
        invoiceId={invoice_id}
        teachers={teachers}
        onCancel={() => setMassUnCertOpen(false)}
        onConfirm={() => {
          setMassUnCertOpen(false);
          fetchTeachers();
        }}
      />

      <SendWelcomeEmailModal
        show={welcomeEmailOpen}
        subscription={selectedSubscription}
        onClose={() => setWelcomeEmailOpen(false)}
      />
    </Container>
  );
}
